//@author: mandy

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    NgZone,
    OnInit,
    ViewEncapsulation,
} from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { Fls } from "../../pojo/Fls";
import { FlsService } from "../../services/fls.service";
import { ReportService } from "../../services/report.service";
import { NotificationService } from "../../services/notification.service";
import { TelemetryService } from "../../services/telemetry.service";
import { ChosenModels, OutputDataI, Type } from "./s25.event.type.util";
import { S25LoadingApi } from "../s25-loading/loading.api";
import { UserprefService } from "../../services/userpref.service";

@TypeManagerDecorator("s25-ng-event-type-list")
@Component({
    selector: "s25-ng-event-type-list",
    template: `
        @if (init) {
            @if (!action || action === undefined) {
                <s25-loading-inline [model]="{}"></s25-loading-inline>
                <h2>{{ headerTitle }} List</h2>
                @if (type === "E" && (canAccessHierarchy || groupId === -1)) {
                    <div class="buttons-wrapper">
                        <s25-toggle-button
                            [falseLabel]="'List View'"
                            [trueLabel]="'Event Types'"
                            (valueChange)="viewModeChanged($event)"
                            [value]="viewMode === 'stdView'"
                        ></s25-toggle-button>

                        @if (hasReportAccess) {
                            <button class="aw-button  aw-button--outline button-spacing" (click)="runReport()">
                                Event Type List Report
                            </button>
                        }
                    </div>
                }

                @if (viewMode === "listView") {
                    <s25-ng-event-type-list-view
                        [type]="type"
                        (valChange)="titleChanged($event)"
                    ></s25-ng-event-type-list-view>
                } @else {
                    <div>
                        <s25-ng-master-definition [type]="'event_type'"></s25-ng-master-definition>
                    </div>
                }
            }

            @if (action && action === "bulkEdit") {
                <s25-loading-inline [model]="{}"></s25-loading-inline>
                <div>
                    <s25-ng-bulk-edit
                        [itemTypeId]="itemTypeId"
                        [itemIds]="selectedItems"
                        [itemTypeName]="headerTitle"
                        [idsOnly]="true"
                        [chosenModels]="chosenModels"
                    ></s25-ng-bulk-edit>

                    <div class="c-margin-top--half">
                        <button
                            class="aw-button aw-button--outline c-margin-top--single c-margin-right--quarter"
                            (click)="returnToList(type)"
                        >
                            Return to List
                        </button>
                    </div>
                </div>
            }
        }
    `,
    styles: `
        .buttons-wrapper {
            display: flex;
            align-items: center;
        }
        .button-spacing {
            margin-left: 100px;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25EventTypeListComponent implements OnInit {
    init = false;
    loading = false;
    viewMode: string = "stdView";
    hasReportAccess = false;
    fls: Fls;
    headerTitle = "Event Types";
    type: Type = Type.EventType;
    action: string = undefined;
    chosenModels: ChosenModels[] = [];
    selectedItems: number[] = [];
    itemTypeId: number = 7;
    groupId: number;
    canAccessHierarchy = false;

    constructor(
        private elementRef: ElementRef,
        private cd: ChangeDetectorRef,
        private zone: NgZone,
    ) {
        this.elementRef.nativeElement.angBridge = this; //bridge to AngularJS; used for AngJS to set model values and call setter fns
    }

    async ngOnInit() {
        S25LoadingApi.init(this.elementRef.nativeElement);
        const [groupId, fls] = await Promise.all([UserprefService.getGroupId(), FlsService.getFls()]);

        this.groupId = groupId;
        this.hasReportAccess = fls.REP_LIST !== "N";

        if (
            fls.ML_EVENT_TYPES === "F" &&
            fls.EVENT_CAB === "F" &&
            fls.EVENT_FLD === "F" &&
            (fls.ML_EVENT === "F" || fls.ML_EVENT === "C") &&
            fls.EVENT_SECURITY === "F"
        ) {
            this.canAccessHierarchy = true;
        }

        this.init = true;
        S25LoadingApi.destroy(this.elementRef.nativeElement);
        this.cd.detectChanges();
    }

    viewModeChanged($event: boolean) {
        if ($event) {
            this.viewMode = "stdView";
            // event types view
            TelemetryService.send("EventTypes", "StdView");
        } else {
            this.viewMode = "listView";
            // list view
            TelemetryService.send("EventTypes", "ListView");
        }
        this.cd.detectChanges();
    }

    runReport() {
        const context = {};
        return ReportService.runReportRequest({
            report: { rpt_id: -215, rpt_engine: "WS", isAsync: false },
            context: context,
            format: null,
            delivery: null,
            otherRecipients: null,
            otherMessage: null,
            query: null,
        }).then(NotificationService.post("Report will be downloaded upon completion"), function () {});
    }

    titleChanged($event: OutputDataI) {
        this.headerTitle = $event.itemName;
        this.type = $event.type;
        this.viewMode = "listView";
        this.action = $event.action;
        this.chosenModels = $event.chosenModels;
        this.selectedItems = $event.selectedItems;
        this.itemTypeId = $event.itemTypId;
        this.cd.detectChanges();
    }

    returnToList(type: Type) {
        this.type = type;
        this.action = undefined;
        this.cd.detectChanges();
    }
}
